/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IResourceDescription, RESOURCE_TYPES } from "@adapptio/lib-core";
import { IEditorResource_Theme, TEditorResource } from "./ResourceTypes";
import { isNonEmptyObject, isNonEmptyString, isString } from "@adapptio/lib-shared";

/**
 * Returns sanitized resource name
 *
 * @param name Resource name
 */
export function sanitizeResourceName(name: string): string {
	return encodeURIComponent(name);
}

/**
 * Returns normalized resource path
 *
 * @param basePath Base path
 * @param name Name
 */
export function normalizeResourcePath(
	basePath: string,
	name: string,
	{ sanitize = false }: { sanitize?: boolean } = {}
): string {
	return basePath + (basePath !== "" ? "/" : "") + (sanitize ? sanitizeResourceName(name) : name);
}

/**
 * Returns normalized resource path
 *
 * @param descriptor Resource descriptor
 */
export function getResourcePath(
	descriptor: IResourceDescription,
	{ sanitize = false }: { sanitize?: boolean } = {}
): string {
	return normalizeResourcePath(descriptor.basePath, descriptor.name, { sanitize });
}

/**
 * Parses and normalized basePath into an array of strings
 *
 * @param basePath Base path
 */
export function parseBasePath(basePath: string): string[] {
	if (basePath === "") {
		return [];
	} else {
		return basePath.split("/");
	}
}

export function isResourceDescriptor(value: unknown): value is IResourceDescription {
	return (
		isNonEmptyObject(value) &&
		isNonEmptyString(value.id) &&
		isNonEmptyString(value.resourceType) &&
		isString(value.basePath) &&
		isNonEmptyObject(value.parseData)
	);
}

export function isResource(value: unknown): value is TEditorResource {
	return (
		isNonEmptyObject(value) &&
		isNonEmptyString(value.id) &&
		isNonEmptyString(value.resourceType) &&
		isResourceDescriptor(value.descriptor)
	);
}

export function isResourceTheme(value: unknown): value is IEditorResource_Theme {
	return isResource(value) && value.resourceType === RESOURCE_TYPES.THEME;
}
